/**
 * Marqueurs : lecture et écriture.
 *
 * Une seule requête, sans paramètre de période — il y a quelques dizaines de
 * marqueurs au plus, et chaque graphique découpe la liste lui-même selon la
 * période affichée. Tous les graphiques de toutes les pages partagent donc la
 * même entrée de cache React Query, et un seul appel réseau.
 *
 * `staleTime` beaucoup plus court que celui des statistiques (5 min) : les
 * stats ne bougent qu'une fois par nuit, un marqueur qu'on vient de poser doit
 * apparaître tout de suite. Les mutations invalident de toute façon la clé.
 */

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import {
  ApiRequestError,
  createMarker,
  deleteMarker,
  fetchMarkers,
  updateMarker,
} from '../lib/api';
import type { Marker, MarkerInput } from '../types';
import { useAuth } from './useAuth';

const MARKERS_KEY = ['markers'] as const;
const THIRTY_SECONDS = 30 * 1000;

export function useMarkers(): UseQueryResult<Marker[], Error> {
  const { token, signOut } = useAuth();

  return useQuery<Marker[], Error>({
    queryKey: MARKERS_KEY,

    queryFn: async ({ signal }) => {
      if (!token) throw new ApiRequestError(401, 'Session expiree');
      try {
        const response = await fetchMarkers(token, signal);
        return response.markers;
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 401) signOut();
        throw error;
      }
    },

    enabled: Boolean(token),
    staleTime: THIRTY_SECONDS,
    refetchOnWindowFocus: false,

    // Une liste vide est un resultat legitime (aucun marqueur pose) : les
    // graphiques doivent s'afficher sans attendre, pas rester en chargement.
    placeholderData: [],

    retry: (failureCount, error) => {
      if (error instanceof ApiRequestError && error.status >= 400 && error.status < 500) {
        return false;
      }
      return failureCount < 2;
    },
  });
}

export interface MarkerMutations {
  create: (input: MarkerInput) => Promise<Marker>;
  update: (id: number, input: MarkerInput) => Promise<Marker>;
  remove: (id: number) => Promise<void>;
  /** `true` pendant qu'une écriture est en cours, pour désactiver le formulaire. */
  isSaving: boolean;
}

/**
 * Les trois écritures, et l'invalidation qui va avec.
 *
 * `invalidateQueries` et non une mise à jour optimiste du cache : l'API
 * normalise ce qu'elle reçoit (libellé rogné, note vide ramenée à `null`,
 * `updated_at` réécrit). Réafficher ce que le formulaire a envoyé ferait
 * divergér l'écran de la base jusqu'au prochain rechargement.
 */
export function useMarkerMutations(): MarkerMutations {
  const { token } = useAuth();
  const queryClient = useQueryClient();

  const requireToken = (): string => {
    if (!token) throw new ApiRequestError(401, 'Session expiree');
    return token;
  };

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: MARKERS_KEY });
  };

  const createMutation = useMutation({
    mutationFn: async (input: MarkerInput) => {
      const response = await createMarker(input, requireToken());
      return response.marker;
    },
    onSuccess: invalidate,
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, input }: { id: number; input: MarkerInput }) => {
      const response = await updateMarker(id, input, requireToken());
      return response.marker;
    },
    onSuccess: invalidate,
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => deleteMarker(id, requireToken()),
    onSuccess: invalidate,
  });

  return {
    create: (input) => createMutation.mutateAsync(input),
    update: (id, input) => updateMutation.mutateAsync({ id, input }),
    remove: (id) => deleteMutation.mutateAsync(id),
    isSaving:
      createMutation.isPending || updateMutation.isPending || deleteMutation.isPending,
  };
}
